import {
  Article,
  ChatCircleText,
  CloudSun,
  Cookie,
  Flag,
  Gear,
  Handshake,
  House,
  Key,
  Lock,
  MapPinArea,
  Plus,
  Scales,
  ShieldCheck,
  Storefront,
  UserCircle
} from "@phosphor-icons/react";
import type { Icon } from "@phosphor-icons/react";
import { reservedUsernames } from "./lib/auth/usernames";

export type RouteDefinition = {
  id: string;
  path: string;
  label: string;
  eyebrow: string;
  title: string;
  description: string;
  icon: Icon;
  nav?: boolean;
  footer?: boolean;
  adminOnly?: boolean;
};

export const routeDefinitions: RouteDefinition[] = [
  {
    id: "spot-map",
    path: "/",
    label: "Spot Map",
    eyebrow: "Spot finder",
    title: "Find water. Track the bite.",
    description: "The root route stays map-first for launch.",
    icon: MapPinArea,
    nav: true
  },
  {
    id: "weather",
    path: "/weather",
    label: "Weather",
    eyebrow: "Weather and conditions",
    title: "Read the water.",
    description: "Live weather, water-data boundaries, and user condition reports.",
    icon: CloudSun,
    nav: true
  },
  {
    id: "resources",
    path: "/resources",
    label: "Resources",
    eyebrow: "Learning library",
    title: "Learn faster.",
    description: "Lessons, guides, diagrams, printable checklists, and field references.",
    icon: Article,
    nav: true
  },
  {
    id: "gear",
    path: "/gear",
    label: "Gear",
    eyebrow: "Curated catalog",
    title: "Browse gear.",
    description: "Curated products, category rails, and transparent partner placements.",
    icon: Storefront,
    nav: true
  },
  {
    id: "community",
    path: "/community",
    label: "Community",
    eyebrow: "Fishing feed",
    title: "Community boards.",
    description: "Public fishing reports, boards, and contribution activity.",
    icon: ChatCircleText,
    nav: true
  },
  {
    id: "partners",
    path: "/partners",
    label: "Partners",
    eyebrow: "Partner workflow",
    title: "Partner with Dahlini.",
    description: "Dedicated partner application route, separate from contact and support.",
    icon: Handshake,
    footer: true
  },
  {
    id: "report",
    path: "/report",
    label: "Report an issue",
    eyebrow: "Trust and safety",
    title: "Report an issue.",
    description: "Private issue reporting for inaccurate, unsafe, abusive, or broken content.",
    icon: Flag
  },
  {
    id: "contact",
    path: "/contact",
    label: "Contact",
    eyebrow: "Support",
    title: "Contact and support.",
    description: "Support form shell for general questions and account help.",
    icon: ChatCircleText,
    footer: true
  },
  {
    id: "about",
    path: "/about",
    label: "About",
    eyebrow: "Brand",
    title: "About Dahlini.",
    description: "Footer-linked brand and origin page shell.",
    icon: House,
    footer: true
  },
  {
    id: "terms",
    path: "/terms",
    label: "Terms",
    eyebrow: "Legal",
    title: "Terms of Service.",
    description: "Legal shell for user-submitted content, moderation, affiliates, and disclaimers.",
    icon: Scales,
    footer: true
  },
  {
    id: "privacy",
    path: "/privacy",
    label: "Privacy",
    eyebrow: "Legal",
    title: "Privacy Policy.",
    description: "Privacy shell for account data, location handling, guest local data, and analytics.",
    icon: ShieldCheck,
    footer: true
  },
  {
    id: "cookies",
    path: "/cookies",
    label: "Cookies",
    eyebrow: "Legal",
    title: "Cookie Policy.",
    description: "Cookie policy shell for analytics, affiliate tracking, and consent controls.",
    icon: Cookie,
    footer: true
  },
  {
    id: "spot-submit",
    path: "/spots/new",
    label: "Submit spot",
    eyebrow: "Spot contribution",
    title: "Submit a fishing spot.",
    description: "Controlled guest and member spot submission for later review.",
    icon: Plus
  },
  {
    id: "login",
    path: "/login",
    label: "Login",
    eyebrow: "Account",
    title: "Sign in.",
    description: "Username/password, email, Apple, and Google account access.",
    icon: UserCircle
  },
  {
    id: "register",
    path: "/register",
    label: "Register",
    eyebrow: "Account",
    title: "Create account.",
    description: "Username, email, and social registration with guest access preserved.",
    icon: UserCircle
  },
  {
    id: "recover",
    path: "/recover",
    label: "Recover account",
    eyebrow: "Account",
    title: "Recover username account.",
    description: "One-time recovery-code flow for username-only accounts.",
    icon: Key
  },
  {
    id: "settings",
    path: "/settings",
    label: "Settings",
    eyebrow: "Account",
    title: "Settings.",
    description: "Profile, credential, recovery, units, attribution, and privacy settings.",
    icon: Gear
  },
  {
    id: "admin",
    path: "/admin",
    label: "Admin",
    eyebrow: "Admin",
    title: "Admin dashboard.",
    description: "Admin shell for moderation queues, imports, support, gear, partners, and users.",
    icon: Lock,
    adminOnly: true
  },
  {
    id: "admin-photos",
    path: "/admin/photos",
    label: "Photo moderation",
    eyebrow: "Admin",
    title: "Photo moderation.",
    description: "Pending community photo review and report jail.",
    icon: Lock,
    adminOnly: true
  },
  {
    id: "admin-reports",
    path: "/admin/reports",
    label: "Report moderation",
    eyebrow: "Admin",
    title: "Report moderation.",
    description: "Condition and issue report review queues with attributed moderation history.",
    icon: Lock,
    adminOnly: true
  }
];

export const reservedRootSegments = new Set(
  routeDefinitions
    .map((route) => route.path.split("/")[1])
    .filter(Boolean)
    .concat(reservedUsernames)
);

export function getNavRoutes() {
  return routeDefinitions.filter((route) => route.nav);
}

export function getFooterRoutes() {
  return routeDefinitions.filter((route) => route.footer);
}

export function resolveRoute(pathname: string) {
  const normalizedPath = normalizePath(pathname);
  const exactRoute = routeDefinitions.find((route) => route.path === normalizedPath);

  if (exactRoute) {
    return {
      type: "static" as const,
      route: exactRoute,
      params: {}
    };
  }

  const segments = normalizedPath.split("/").filter(Boolean);

  if (segments[0] === "spots" && segments[1]) {
    return {
      type: "spot-detail" as const,
      route: dynamicRoute("spot-detail", "Spot detail", "Verified public spot.", "Public spot conditions, access, species, and location.", MapPinArea),
      params: { slug: segments[1] }
    };
  }

  if (segments[0] === "resources" && segments[1]) {
    return {
      type: "resource-detail" as const,
      route: dynamicRoute("resource-detail", "Resource detail", "Field guide", "Public fishing lesson and field reference.", Article),
      params: { slug: segments[1] }
    };
  }

  if (segments[0] === "gear" && segments[1]) {
    return {
      type: "gear-category" as const,
      route: dynamicRoute("gear-category", "Gear category", "Gear category", "Source-ready public gear category framework.", Storefront),
      params: { category: segments[1] }
    };
  }

  if (segments[0] === "community" && segments[1]) {
    return {
      type: "community-post" as const,
      route: dynamicRoute("community-post", "Community post", "Community post", "Public community post preview.", ChatCircleText),
      params: { slug: segments[1] }
    };
  }

  if (segments.length === 1 && !reservedRootSegments.has(segments[0].toLowerCase())) {
    return {
      type: "username" as const,
      route: dynamicRoute("profile", "Profile", "Public profile", "Public community profile preview.", UserCircle),
      params: { username: segments[0] }
    };
  }

  return {
    type: "not-found" as const,
    route: dynamicRoute("not-found", "Not found", "Not found.", "This route is not part of the Dahlini scaffold.", ShieldCheck),
    params: {}
  };
}

function normalizePath(pathname: string) {
  const withoutTrailingSlash = pathname.replace(/\/+$/, "");
  return withoutTrailingSlash || "/";
}

function dynamicRoute(
  id: string,
  label: string,
  title: string,
  description: string,
  icon: Icon
): RouteDefinition {
  return {
    id,
    path: "",
    label,
    eyebrow: "Route scaffold",
    title,
    description,
    icon
  };
}
